// <pos-reports/> — 營收報表（日 / 月 / 年）
// Pat 2026-04-22 插隊需求：簡易版毛利報表
// see architecture.md#L280 (reports-revenue API) + docs/project_revenue_reports_ask.md

import { fetchRevenueReport } from '../lib/api.js';

const escapeHtml = (s) => String(s ?? '').replace(/[&<>"']/g, (c) =>
  ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

class PosReports extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.state = {
      period: 'day',
      date: isoDate(new Date()),
      loading: false,
      error: null,
      data: null,
    };
  }

  connectedCallback() {
    const role = document.body.dataset.role || 'cashier';
    if (role !== 'owner' && role !== 'manager') {
      location.hash = '#/dashboard';
      return;
    }
    this.render();
    this.load();
  }

  async load() {
    this.state.loading = true;
    this.state.error = null;
    this.render();
    try {
      this.state.data = await fetchRevenueReport({
        period: this.state.period,
        date: this.state.date,
      });
    } catch (err) {
      this.state.error = err.message_zh || err.message || '載入失敗';
      this.state.data = null;
    } finally {
      this.state.loading = false;
      this.render();
    }
  }

  onPeriodChange(period) {
    this.state.period = period;
    // 不同 period 預設不同 date 格式的輸入語意，但後端都吃 YYYY-MM-DD
    // 切 period 保持現有 date；若需要可自動 normalize
    this.load();
  }

  onDateChange(v) {
    // input[type=date] 永遠吐 YYYY-MM-DD
    if (!/^\d{4}-\d{2}-\d{2}$/.test(v)) return;
    this.state.date = v;
    this.load();
  }

  render() {
    const { period, date, loading, error, data } = this.state;

    const summary = data?.summary;
    const byItem = data?.by_item || [];
    const marginPct = summary?.gross_margin_pct;
    const noCost = summary && summary.cogs === 0 && summary.revenue > 0;

    this.shadowRoot.innerHTML = `
      <style>
        :host { display: block; padding: var(--s-5); max-width: 1200px; margin: 0 auto; }
        h2 { margin: 0 0 var(--s-4); font-family: var(--font-display); color: var(--brand-primary); }
        .bar { display: flex; gap: var(--s-3); align-items: center; flex-wrap: wrap; margin-bottom: var(--s-5); }
        .seg { display: inline-flex; border: 1px solid var(--border); border-radius: var(--r-md); overflow: hidden; }
        .seg button {
          background: transparent; color: var(--fg-muted); border: 0; padding: var(--s-2) var(--s-4);
          cursor: pointer; min-height: var(--touch-min);
        }
        .seg button[aria-pressed="true"] { background: var(--brand-primary); color: white; }
        input[type="date"] {
          padding: var(--s-2) var(--s-3); background: var(--bg-surface); color: var(--fg);
          border: 1px solid var(--border); border-radius: var(--r-md); min-height: var(--touch-min);
        }
        .status { color: var(--fg-muted); font-size: 14px; }
        .err { color: var(--danger); padding: var(--s-3); background: rgba(220,38,38,.1); border-radius: var(--r-md); }

        .grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: var(--s-4); margin-bottom: var(--s-6); }
        .stat { padding: var(--s-5); background: var(--bg-elevated); border-radius: var(--r-lg); }
        .stat h3 { color: var(--fg-muted); font-size: 13px; margin: 0 0 var(--s-2); font-weight: 500; }
        .stat .v { font-size: 28px; font-weight: 600; color: var(--brand-primary); line-height: 1.1; }
        .stat .sub { margin-top: var(--s-1); color: var(--fg-muted); font-size: 12px; }

        .notice { padding: var(--s-3); background: rgba(59,130,246,.1); border-radius: var(--r-md); margin-bottom: var(--s-5); color: var(--fg); font-size: 14px; }

        h3.sec { margin: var(--s-6) 0 var(--s-3); color: var(--fg); }

        table { width: 100%; border-collapse: collapse; background: var(--bg-elevated); border-radius: var(--r-lg); overflow: hidden; }
        th, td { padding: var(--s-3) var(--s-4); text-align: left; border-bottom: 1px solid var(--border); font-size: 14px; }
        th { background: var(--bg-surface); color: var(--fg-muted); font-weight: 500; font-size: 12px; text-transform: uppercase; letter-spacing: .05em; }
        td.num { text-align: right; font-variant-numeric: tabular-nums; }
        tr:last-child td { border-bottom: 0; }
        .margin-good { color: #10b981; font-weight: 600; }
        .margin-warn { color: #f59e0b; font-weight: 600; }
        .margin-bad { color: var(--danger); font-weight: 600; }
        .muted { color: var(--fg-muted); font-size: 12px; }

        .empty { padding: var(--s-8); text-align: center; color: var(--fg-muted); background: var(--bg-elevated); border-radius: var(--r-lg); }
      </style>

      <h2>營收報表</h2>

      <div class="bar">
        <div class="seg" role="tablist">
          ${['day', 'week', 'month', 'year'].map(p => `
            <button type="button" data-period="${p}" aria-pressed="${period === p}">${periodLabel(p)}</button>
          `).join('')}
        </div>
        <input type="date" id="d" value="${escapeHtml(date)}" />
        ${loading ? '<span class="status">載入中…</span>' : ''}
      </div>

      ${error ? `<div class="err">✗ ${escapeHtml(error)}</div>` : ''}

      ${!data || !summary ? '' : `
        ${noCost ? `<div class="notice">ℹ️ 有訂單但未設品項成本，毛利顯示為 100%。到「菜單」編輯各品項填入成本後重新整理。</div>` : ''}

        <div class="grid">
          <div class="stat">
            <h3>營收 (${escapeHtml(data.label)})</h3>
            <div class="v">NT$ ${formatNum(summary.revenue)}</div>
            <div class="sub">${summary.order_count} 筆有效訂單</div>
          </div>
          <div class="stat">
            <h3>食材成本</h3>
            <div class="v">NT$ ${formatNum(summary.cogs)}</div>
          </div>
          <div class="stat">
            <h3>毛利</h3>
            <div class="v">NT$ ${formatNum(summary.gross_profit)}</div>
          </div>
          <div class="stat">
            <h3>毛利率</h3>
            <div class="v ${marginClass(marginPct)}">${marginPct == null ? '—' : `${marginPct.toFixed(1)}%`}</div>
          </div>
        </div>

        <h3 class="sec">品項明細（依營收排序）</h3>
        ${byItem.length === 0 ? '<div class="empty">這段期間還沒有交易。<a href="#/pos" style="color:var(--brand-primary)">去點餐頁開始第一筆訂單 →</a></div>' : `
          <table>
            <thead>
              <tr>
                <th>品項</th>
                <th class="num">銷量</th>
                <th class="num">營收</th>
                <th class="num">成本</th>
                <th class="num">毛利</th>
                <th class="num">毛利率</th>
              </tr>
            </thead>
            <tbody>
              ${byItem.map(r => `
                <tr>
                  <td>${escapeHtml(r.name)}${r.cost_set ? '' : '<div class="muted">未設成本</div>'}</td>
                  <td class="num">${r.qty_sold}</td>
                  <td class="num">NT$ ${formatNum(r.revenue)}</td>
                  <td class="num">NT$ ${formatNum(r.cogs)}</td>
                  <td class="num">NT$ ${formatNum(r.gross_profit)}</td>
                  <td class="num ${marginClass(r.gross_margin_pct)}">
                    ${r.gross_margin_pct == null ? '—' : `${r.gross_margin_pct.toFixed(1)}%`}
                  </td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        `}
      `}
    `;

    // wire handlers
    this.shadowRoot.querySelectorAll('button[data-period]').forEach(btn => {
      btn.addEventListener('click', () => this.onPeriodChange(btn.dataset.period));
    });
    const dateInput = this.shadowRoot.querySelector('#d');
    if (dateInput) {
      dateInput.addEventListener('change', (e) => this.onDateChange(e.target.value));
    }
  }
}

function isoDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function periodLabel(p) {
  return { day: '日', week: '週', month: '月', year: '年' }[p] ?? p;
}

function formatNum(n) {
  const v = Number(n || 0);
  return v.toLocaleString('zh-TW', { maximumFractionDigits: 2 });
}

function marginClass(pct) {
  if (pct == null) return '';
  if (pct >= 50) return 'margin-good';
  if (pct >= 25) return 'margin-warn';
  return 'margin-bad';
}

customElements.define('pos-reports', PosReports);
